import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { ArrowLeft, Briefcase, Mail, Smartphone } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { BackendServerSelector } from '@/components/auth/backend-server-selector'
import { useSelectedBackendServerUrl } from '@/lib/backend-servers'
import { authApi } from '@/services/auth'
import { orgTreeApi } from '@/services/org-tree'
import { getErrorMessage } from '@/lib/api'
import { getAuthThemeServiceInstanceId, useAuthTheme } from '@/lib/auth-theme'
import { resolveServiceInstanceAuthLogos } from '@/lib/auth-branding'
import { TenantAuthShell } from '@/components/auth/tenant-auth-shell'

const forgotPasswordSchema = z.object({
  email: z.string().trim().optional().or(z.literal('')),
  mobile: z.string().trim().optional().or(z.literal('')),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>
type ResetMethod = 'email' | 'mobile'
type SubmissionMode = 'otp' | 'link' | null

const getOtpLength = (rawValue: unknown) => {
  const parsedValue = Number(rawValue)
  if (!Number.isFinite(parsedValue) || parsedValue < 4) {
    return 6
  }

  return Math.min(parsedValue, 8)
}

export default function ForgotPasswordPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [submissionMode, setSubmissionMode] = useState<SubmissionMode>(null)
  const [selectedMethod, setSelectedMethod] = useState<ResetMethod>('email')
  const [submittedCredential, setSubmittedCredential] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [otpLength, setOtpLength] = useState(6)
  const [otpErrorMessage, setOtpErrorMessage] = useState('')
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false)
  const [submissionMessage, setSubmissionMessage] = useState('')
  const selectedBackendServerUrl = useSelectedBackendServerUrl()
  const authTheme = useAuthTheme()
  const searchParams = new URLSearchParams(location.search)
  const urlTenant = searchParams.get('org')
    || searchParams.get('id')
    || searchParams.get('tenant')
    || searchParams.get('company_identifier')
    || searchParams.get('service_instance_id')
    || ''
  const themeTenant = authTheme.locked ? getAuthThemeServiceInstanceId(authTheme) : ''
  const queryTenant = themeTenant || urlTenant
  const deferredTenant = useDeferredValue(queryTenant.trim())

  const organizationQuery = useQuery({
    queryKey: ['forgot-password-service-instance', selectedBackendServerUrl, deferredTenant],
    queryFn: () => orgTreeApi.resolvePublicSignupServiceInstance(deferredTenant),
    enabled: deferredTenant.length >= 3,
    staleTime: 60_000,
    retry: false,
  })

  const organization = (authTheme.locked ? authTheme.organization : null) ?? organizationQuery.data
  const organizationName = organization?.name
    || organization?.title
    || organization?.identifier
    || organization?.url_identifier
    || 'Ellan One'
  const { sidePanelLogos, welcomeLogos } = resolveServiceInstanceAuthLogos(organization)
  const primaryColor = organization?.primary_color || authTheme.primaryColor
  const loginTenant = organization?.identifier || organization?.instance_id || queryTenant
  const loginHref = loginTenant
    ? `/login?tenant=${encodeURIComponent(loginTenant)}`
    : '/login'
  const orgInstanceId = organization?.instance_id || String(organization?.id ?? '').trim() || undefined
  const orgCountryId = Number(organization?.country_id)

  const loginWithEmail = organization?.login_with_email === true
  const loginWithMobile = organization?.login_with_mobile === true
  const passwordResetActive = organization?.pwdreset_active !== false
  const adminOnlyReset = organization?.pwdreset_third_party === true
  const resetFlowType = Number(organization?.pwdreset_type) === 1 ? 'link' : 'otp'
  const availableMethods = useMemo<ResetMethod[]>(() => {
    const methods: ResetMethod[] = []
    if (loginWithEmail) methods.push('email')
    if (loginWithMobile) methods.push('mobile')
    if (organization) {
      return methods
    }
    return methods.length > 0 ? methods : ['email']
  }, [loginWithEmail, loginWithMobile, organization])
  const hasSelfResetMethod = availableMethods.length > 0

  useEffect(() => {
    if (!availableMethods.includes(selectedMethod)) {
      setSelectedMethod(availableMethods[0])
    }
  }, [availableMethods, selectedMethod])

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '', mobile: '' },
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    if (adminOnlyReset) {
      toast.error('Self password recovery is disabled for this organization. Contact your administrator.')
      return
    }
    if (!hasSelfResetMethod) {
      toast.error('Password recovery is not configured for email or mobile self-service on this organization.')
      return
    }
    if (!passwordResetActive) {
      toast.error('Password recovery is currently disabled for this organization.')
      return
    }

    const normalizedEmail = (data.email ?? '').trim()
    const normalizedMobile = (data.mobile ?? '').replace(/\D/g, '')
    clearErrors()

    if (selectedMethod === 'email') {
      const emailCheck = z.string().email('Please enter a valid email address').safeParse(normalizedEmail)
      if (!emailCheck.success) {
        setError('email', { type: 'validate', message: 'Please enter a valid email address' })
        return
      }
    } else {
      if (normalizedMobile.length < 6) {
        setError('mobile', { type: 'validate', message: 'Please enter a valid mobile number' })
        return
      }
      if (!Number.isFinite(orgCountryId) || orgCountryId <= 0) {
        toast.error('Country configuration is missing for this organization. Please contact your administrator.')
        return
      }
    }

    setIsLoading(true)
    try {
      const response = await authApi.forgotPassword({
        org_instance_id: orgInstanceId,
        ...(selectedMethod === 'email'
          ? { email: normalizedEmail }
          : { phn_nbr: normalizedMobile, country_id: orgCountryId }),
      })

      const context = String(response.data?.context ?? '').toLowerCase()
      const isOtpResponse = context.includes('otp') || (!context.includes('link') && resetFlowType === 'otp')
      const isLinkResponse = context.includes('link') || (!isOtpResponse && resetFlowType === 'link')
      const credential = selectedMethod === 'email' ? normalizedEmail : normalizedMobile

      setOtpLength(getOtpLength(response.data?.otp_length))
      setSubmittedCredential(credential)
      setOtpCode('')
      setOtpErrorMessage('')
      setSubmissionMessage(response.message || response.data?.message || '')
      setSubmissionMode(isOtpResponse ? 'otp' : isLinkResponse ? 'link' : resetFlowType)
      if (isLinkResponse) {
        toast.success(response.message || 'Verification link sent successfully.')
      }
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyOtp = async () => {
    const normalizedCode = otpCode.trim()
    if (!submittedCredential) {
      setOtpErrorMessage('Identifier is missing. Request a new verification code.')
      return
    }

    if (normalizedCode.length !== otpLength) {
      setOtpErrorMessage(`Enter the ${otpLength}-digit verification code.`)
      return
    }

    setOtpErrorMessage('')
    setIsVerifyingOtp(true)
    try {
      const response = selectedMethod === 'email'
        ? await authApi.verifyForgotPasswordEmailOtp({
            email: submittedCredential,
            code: normalizedCode,
          })
        : await authApi.verifyForgotPasswordPhoneOtp({
            org_instance_id: orgInstanceId || '',
            country_id: Number.isFinite(orgCountryId) ? orgCountryId : 0,
            phn_nbr: submittedCredential,
            code: normalizedCode,
          })
      const token = String(response.data?.token ?? '').trim()
      if (!token) {
        throw new Error('Verification token was not returned. Please request a new code.')
      }

      const resetParams = new URLSearchParams()
      resetParams.set('token', token)
      resetParams.set('credential', submittedCredential)
      resetParams.set('login_method', selectedMethod)
      if (loginTenant) {
        resetParams.set('tenant', loginTenant)
      }
      if (organization?.instance_id) {
        resetParams.set('org', organization.instance_id)
      }

      navigate(`/reset-password?${resetParams.toString()}`, { replace: true })
    } catch (error) {
      setOtpErrorMessage(getErrorMessage(error))
    } finally {
      setIsVerifyingOtp(false)
    }
  }

  const handleResendCode = async () => {
    if (!submittedCredential) {
      setOtpErrorMessage('Identifier is missing. Request a new verification code.')
      return
    }

    setOtpErrorMessage('')
    setIsLoading(true)
    try {
      const response = await authApi.forgotPassword({
        org_instance_id: orgInstanceId,
        ...(selectedMethod === 'email'
          ? { email: submittedCredential }
          : { phn_nbr: submittedCredential, country_id: orgCountryId }),
      })
      setOtpLength(getOtpLength(response.data?.otp_length))
      setOtpCode('')
      toast.success(response.message || 'A new verification code has been sent.')
    } catch (error) {
      setOtpErrorMessage(getErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  if (submissionMode === 'otp') {
    const targetLabel = selectedMethod === 'email' ? 'email' : 'mobile'
    return (
      <TenantAuthShell
        organizationName={organizationName}
        sidePanelLogos={sidePanelLogos}
        welcomeLogos={welcomeLogos}
        primaryColor={primaryColor}
        icon={Mail}
        eyebrow="Password recovery"
        title={`Verify your ${targetLabel}`}
        description={`Enter the ${otpLength}-digit code sent to your ${targetLabel} to continue.`}
        panelTitle="verification"
        panelDescription={(
          <>
            We sent a verification code to <span className="font-medium text-foreground">{submittedCredential}</span>
          </>
        )}
        logoPosition="left"
        contentPanelClassName="rounded-[24px] bg-white/95 px-5 py-5 text-foreground shadow-xl shadow-slate-950/15 backdrop-blur sm:px-6 sm:py-6"
        sideActions={(
          <Link to={loginHref} className="inline-flex items-center rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/30 transition hover:bg-white/25">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to sign in
          </Link>
        )}
      >
        <Card className="border-0 bg-transparent shadow-none">
          <CardContent className="px-0">
            <BackendServerSelector disabled={isLoading || isVerifyingOtp} className="mb-4" />
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot_password_otp" className="sr-only">Verification code</Label>
                <Input
                  id="forgot_password_otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={otpLength}
                  className="h-11 rounded-full border-0 bg-primary/15 px-5 text-center tracking-[0.45em] shadow-none placeholder:text-primary/45 focus-visible:ring-primary/35"
                  placeholder={`${otpLength}-digit code`}
                  value={otpCode}
                  disabled={isLoading || isVerifyingOtp}
                  onChange={(event) => {
                    setOtpCode(event.target.value.replace(/\D/g, '').slice(0, otpLength))
                    if (otpErrorMessage) {
                      setOtpErrorMessage('')
                    }
                  }}
                  aria-invalid={Boolean(otpErrorMessage)}
                />
                {otpErrorMessage ? <p className="text-center text-sm text-destructive">{otpErrorMessage}</p> : null}
              </div>

              <Button
                type="button"
                className="mx-auto flex h-11 min-w-44 rounded-full px-8"
                disabled={isLoading || isVerifyingOtp}
                onClick={handleVerifyOtp}
              >
                {isVerifyingOtp ? <Spinner className="mr-2" /> : null}
                Verify code
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                Didn&apos;t receive the email? Check your spam folder or{' '}
                <button
                  type="button"
                  className="font-semibold text-primary hover:underline disabled:opacity-60"
                  disabled={isLoading || isVerifyingOtp}
                  onClick={handleResendCode}
                >
                  resend code
                </button>
                .
              </div>

              <Button
                variant="outline"
                className="mx-auto flex h-11 min-w-56 rounded-full px-8"
                disabled={isLoading || isVerifyingOtp}
                onClick={() => {
                  setSubmissionMode(null)
                  setSubmittedCredential('')
                  setOtpCode('')
                  setOtpErrorMessage('')
                }}
              >
                Try another method
              </Button>
            </div>
          </CardContent>
        </Card>
      </TenantAuthShell>
    )
  }

  if (submissionMode === 'link') {
    return (
      <TenantAuthShell
        organizationName={organizationName}
        sidePanelLogos={sidePanelLogos}
        welcomeLogos={welcomeLogos}
        primaryColor={primaryColor}
        icon={Mail}
        eyebrow="Password recovery"
        title="Check your inbox"
        description="A verification link has been sent. Use it to continue."
        panelTitle="verification link"
        panelDescription={(
          <>
            {submissionMessage || 'We sent a verification link to'}{' '}
            <span className="font-medium text-foreground">{submittedCredential}</span>
          </>
        )}
        logoPosition="left"
        contentPanelClassName="rounded-[24px] bg-white/95 px-5 py-5 text-foreground shadow-xl shadow-slate-950/15 backdrop-blur sm:px-6 sm:py-6"
        sideActions={(
          <Link to={loginHref} className="inline-flex items-center rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/30 transition hover:bg-white/25">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to sign in
          </Link>
        )}
      >
        <Card className="border-0 bg-transparent shadow-none">
          <CardContent className="px-0 space-y-4">
            <BackendServerSelector disabled={false} className="mb-2" />
            <p className="rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-primary">
              {submissionMessage || 'Please wait for the verification link and follow the instructions in the email.'}
            </p>
            <Button
              variant="outline"
              className="mx-auto flex h-11 min-w-56 rounded-full px-8"
              onClick={() => {
                setSubmissionMode(null)
                setSubmittedCredential('')
                setSubmissionMessage('')
              }}
            >
              Try another method
            </Button>
          </CardContent>
        </Card>
      </TenantAuthShell>
    )
  }

  return (
    <TenantAuthShell
      organizationName={organizationName}
      sidePanelLogos={sidePanelLogos}
      welcomeLogos={welcomeLogos}
      primaryColor={primaryColor}
      icon={Briefcase}
      eyebrow="Password recovery"
      title="Forgot password?"
      description={organizationQuery.isLoading
        ? 'Resolving organization details...'
        : adminOnlyReset
          ? `Password recovery for ${organizationName} is managed by administrators.`
          : `No worries, we will send verification instructions for ${organizationName}.`}
      panelTitle="password"
      panelDescription={adminOnlyReset
        ? 'Contact your organization administrator'
        : selectedMethod === 'mobile'
          ? 'Enter your mobile number'
          : 'Enter your email address'}
      logoPosition="left"
      contentPanelClassName="rounded-[24px] bg-white/95 px-5 py-5 text-foreground shadow-xl shadow-slate-950/15 backdrop-blur sm:px-6 sm:py-6"
      sideActions={(
        <Link to={loginHref} className="inline-flex items-center rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white ring-1 ring-white/30 transition hover:bg-white/25">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to sign in
        </Link>
      )}
    >
      <Card className="border-0 bg-transparent shadow-none">
        <CardHeader className="sr-only">
          <CardTitle>Forgot password</CardTitle>
          <CardDescription>Enter your email or mobile number based on organization configuration.</CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          <BackendServerSelector disabled={isLoading} className="mb-4" />
          {queryTenant && !organizationQuery.isLoading && !organization ? (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              We could not verify that organization reference. You can still continue, but the organization may need to be entered again on sign-in.
            </div>
          ) : null}
          {!passwordResetActive ? (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Password recovery is currently disabled for this organization.
            </div>
          ) : null}
          {adminOnlyReset ? (
            <div className="mb-4 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
              Only administrators can change passwords for this organization. Please contact your admin for assistance.
            </div>
          ) : null}
          {!adminOnlyReset && availableMethods.length > 1 ? (
            <div className="mb-4 grid grid-cols-2 gap-2 rounded-2xl border border-border/70 bg-muted/20 p-2">
              <button
                type="button"
                className={`h-10 rounded-xl text-sm font-semibold transition ${selectedMethod === 'email' ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground'}`}
                onClick={() => setSelectedMethod('email')}
                disabled={isLoading}
              >
                <Mail className="mr-1 inline h-4 w-4" />
                Email
              </button>
              <button
                type="button"
                className={`h-10 rounded-xl text-sm font-semibold transition ${selectedMethod === 'mobile' ? 'bg-primary text-primary-foreground' : 'bg-background text-foreground'}`}
                onClick={() => setSelectedMethod('mobile')}
                disabled={isLoading}
              >
                <Smartphone className="mr-1 inline h-4 w-4" />
                Mobile
              </button>
            </div>
          ) : null}
          {!adminOnlyReset && hasSelfResetMethod === false ? (
            <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              This organization has not enabled self password recovery via email or mobile. Please contact an administrator.
            </div>
          ) : null}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {selectedMethod === 'email' ? (
              <div className="space-y-2">
                <Label htmlFor="email" className="sr-only">Email</Label>
                <Input
                  id="email"
                  className="h-11 rounded-full border-0 bg-primary/15 px-5 text-center shadow-none placeholder:text-primary/45 focus-visible:ring-primary/35"
                  type="email"
                  placeholder="Email"
                  autoComplete="email"
                  disabled={isLoading || adminOnlyReset || !passwordResetActive}
                  {...register('email')}
                  aria-invalid={!!errors.email}
                />
                {errors.email && (
                  <p className="text-center text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="mobile" className="sr-only">Mobile number</Label>
                <Input
                  id="mobile"
                  className="h-11 rounded-full border-0 bg-primary/15 px-5 text-center shadow-none placeholder:text-primary/45 focus-visible:ring-primary/35"
                  type="tel"
                  placeholder="Mobile number"
                  autoComplete="tel"
                  disabled={isLoading || adminOnlyReset || !passwordResetActive}
                  {...register('mobile', {
                    onChange: (event) => {
                      event.target.value = event.target.value.replace(/\D/g, '')
                    },
                  })}
                  aria-invalid={!!errors.mobile}
                />
                <p className="text-center text-xs text-muted-foreground">
                  Enter your local mobile number without country code.
                </p>
                {errors.mobile && (
                  <p className="text-center text-sm text-destructive">{errors.mobile.message}</p>
                )}
              </div>
            )}

            {!adminOnlyReset && passwordResetActive ? (
              <p className="text-center text-xs text-muted-foreground">
                {resetFlowType === 'otp'
                  ? 'You will be asked to enter a verification code after submission.'
                  : 'You will receive a verification link and can continue once it arrives.'}
              </p>
            ) : null}

            <Button
              type="submit"
              className="mx-auto flex h-11 min-w-44 rounded-full px-8"
              disabled={isLoading || adminOnlyReset || !passwordResetActive || !hasSelfResetMethod}
            >
              {isLoading && <Spinner className="mr-2" />}
              {resetFlowType === 'otp' ? 'Send verification code' : 'Send verification link'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </TenantAuthShell>
  )
}
